import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = ({ collapsed, onToggle }) => {
  const { logout, userRole } = useAuth();
  const isManager = userRole === 'manager';

  const SIDEBAR_BG = '#031128'; // Dark blue from theme
  const SIDEBAR_BORDER = 'rgba(255, 255, 255, 0.1)';
  const SIDEBAR_PRIMARY = '#568ded';

  // Keep a global CSS variable for sidebar width so pages can align their content
  React.useEffect(() => {
    const width = collapsed ? '60px' : '240px';
    document.documentElement.style.setProperty('--sidebar-width', width);
  }, [collapsed]);

  const NavItem = ({ to, icon, customIcon, label }) => (
    <NavLink 
      to={to} 
      className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 5px',
        textDecoration: 'none',
        color: isActive ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)',
        backgroundColor: isActive ? 'rgba(86, 141, 237, 0.15)' : 'transparent',
        borderLeft: 'none', // Removed border left, using background radius like employer
        borderRadius: '8px',
        transition: 'all 0.2s',
        marginBottom: '4px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        width: '200px', // Fixed width matching content box
        boxSizing: 'border-box',
        justifyContent: 'flex-start'
      })}
    >
      <div style={{ width: '20px', minWidth: '20px', display: 'flex', justifyContent: 'center' }}>
        {customIcon ? customIcon : <i className={`fa-solid ${icon}`} style={{ color: SIDEBAR_PRIMARY }}></i>}
      </div>
      <span style={{ 
        opacity: collapsed ? 0 : 1, 
        transition: 'opacity 0.2s',
        fontFamily: 'Montserrat',
        fontSize: '14px',
        fontWeight: 500
      }}>
        {label}
      </span>
    </NavLink>
  );

  return (
    <>
      <aside className="portal-sidebar" style={{
        width: collapsed ? '60px' : '240px',
        height: '100vh',
        backgroundColor: SIDEBAR_BG,
        borderRight: `1px solid ${SIDEBAR_BORDER}`,
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1000,
        transition: 'width 250ms ease',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
        padding: '20px', // Match Employer App padding
        paddingRight: '10px'
      }}>
        {/* Brand Header */}
        <div style={{ 
          height: '40px', // Adjusted height
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '40px', // Spacing instead of border
          width: '200px', // Fixed width
          gap: '10px',
          paddingLeft: '5px'
        }}>
          <div style={{ width: '20px', minWidth: '20px', display: 'flex', justifyContent: 'center' }}>
            <i className="fa-solid fa-cube" style={{ color: SIDEBAR_PRIMARY, fontSize: '24px' }}></i>
          </div>
          <span style={{ 
            color: '#FFFFFF', 
            fontFamily: 'Montserrat', 
            fontWeight: 800, 
            fontSize: '18px',
            opacity: collapsed ? 0 : 1,
            transition: 'opacity 0.2s',
            whiteSpace: 'nowrap'
          }}>
        JOBSEEQR
          </span>
      </div>
      
        {/* Navigation */}
        <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <NavItem to="/" icon="fa-house" label="Dashboard" />
          <NavItem to="/onboarding" icon="fa-list-check" label="Onboarding" />
          <NavItem to="/time-off" icon="fa-plane" label="Time Off" />
          <NavItem to="/documents" icon="fa-file-lines" label="Documents" />
          <NavItem to="/profile" icon="fa-user" label="Profile" />
        
        {isManager && (
            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: `1px solid ${SIDEBAR_BORDER}` }}>
              <NavItem 
                to="/manager" 
                label="Manager Hub" 
                customIcon={
                  <div style={{ position: 'relative', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fa-solid fa-users" style={{ color: SIDEBAR_PRIMARY, fontSize: '16px' }}></i>
                  </div>
                }
              />
            </div>
        )}
      </nav>

        {/* Footer Actions */}
        <div style={{ 
          paddingTop: '20px', 
          borderTop: `1px solid ${SIDEBAR_BORDER}`,
          width: '200px'
        }}>
          <button 
            onClick={logout} 
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer', 
              width: '100%', 
              display: 'flex',
              alignItems: 'center', 
              gap: '12px',
              color: 'rgba(255, 255, 255, 0.6)',
              padding: '8px 0',
              transition: 'color 0.2s',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => e.target.style.color = '#fff'}
            onMouseLeave={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.6)'}
          >
            <div style={{ width: '20px', minWidth: '20px', display: 'flex', justifyContent: 'center' }}>
              <i className="fa-solid fa-right-from-bracket"></i>
            </div>
            <span style={{ 
              opacity: collapsed ? 0 : 1, 
              transition: 'opacity 0.2s',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}>
          Sign Out
            </span>
          </button>
        </div>

        <button
          onClick={() => onToggle(!collapsed)}
          aria-label="Toggle sidebar"
          style={{
            position: 'fixed',
            top: '10px',
            left: collapsed ? '60px' : '240px',
            width: '40px',
            height: '40px',
            background: SIDEBAR_BG,
            borderTop: `1px solid ${SIDEBAR_BORDER}`,
            borderRight: `1px solid ${SIDEBAR_BORDER}`,
            borderBottom: `1px solid ${SIDEBAR_BORDER}`,
            borderLeft: 'none',
            borderRadius: '0 8px 8px 0',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            transition: 'left 250ms ease'
          }}
        >
          <i
            className="fa-solid fa-circle-chevron-left"
            style={{
              color: SIDEBAR_PRIMARY,
              fontSize: '20px',
              transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 250ms ease'
            }}
          ></i>
        </button>
    </aside>
    </>
  );
};

export default Sidebar;
